import { normalizeGallery, type GalleryItem } from "@/lib/contentMedia";
import { supabase } from "@/integrations/supabase/client";
import { localize } from "@/lib/localizedFields";

export interface CmsIndustry {
  id: string; slug: string; title: string; subtitle: string | null; description: string | null;
  hero_image_url: string | null; use_cases: unknown[]; case_studies: unknown[];
  seo_title: string | null; seo_description: string | null; video_url: string | null; gallery: GalleryItem[]; content_format?: string;
  published_at: string | null; sort_order: number | null;
  /** Structured page sections for template-based pages (imported bundled industries). */
  template: Record<string, any> | null;
}
export interface IndustryLink { id: string; module_slug: string | null; addon: { slug: string; name: string } | null; pain: string; outcome: string; sort_order: number }

/** Public visibility rule shared by index and detail: published and not scheduled in the future. */
export const isLive = (r: { is_published: boolean | null; published_at: string | null }, now = Date.now()) =>
  !!r.is_published && (!r.published_at || Date.parse(r.published_at) <= now);

const TEXT_FIELDS = ["title", "subtitle", "description", "seo_title", "seo_description"] as const;

function shape(row: Record<string, any>, locale: string, tr?: Record<string, unknown>): CmsIndustry {
  const legacyAr = locale === "ar" ? { title: row.title_ar, subtitle: row.subtitle_ar, description: row.description_ar } : undefined;
  const base = Object.fromEntries(TEXT_FIELDS.map((k) => [k, row[k] ?? ""])) as Record<string, string>;
  const l = localize(localize(base, legacyAr), tr);
  return {
    id: row.id, slug: row.slug, title: l.title, subtitle: l.subtitle || null, description: l.description || null,
    seo_title: l.seo_title || null, seo_description: l.seo_description || null,
    hero_image_url: row.hero_image_url, use_cases: Array.isArray(row.use_cases) ? row.use_cases : [],
    case_studies: Array.isArray(row.case_studies) ? row.case_studies : [], video_url: row.video_url,
    gallery: normalizeGallery(row.gallery), content_format: row.content_format,
    published_at: row.published_at, sort_order: row.sort_order,
    template: row.template && typeof row.template === "object" ? localizeTemplate(row.template, tr?.template) : null,
  };
}

/** Merges a translated template (same shape) over the source; untranslated parts stay as source text. */
function localizeTemplate(src: Record<string, any>, tr: unknown): Record<string, any> {
  if (!tr || typeof tr !== "object") return src;
  const out: Record<string, any> = { ...src };
  for (const [k, v] of Object.entries(tr as Record<string, any>)) {
    if (typeof v === "string" && v.trim()) out[k] = v;
    else if (Array.isArray(v) && v.length) out[k] = v;
    else if (v && typeof v === "object" && src[k] && typeof src[k] === "object") out[k] = localizeTemplate(src[k], v);
  }
  return out;
}

/** Slugs of formerly bundled industries that editors unpublished/scheduled; their bundled fallback must stay hidden. */
export async function hiddenStaticIndustrySlugs(): Promise<Set<string> | null> {
  const { data, error } = await supabase.rpc("industries_hidden_static_slugs");
  if (error) return null;
  return new Set(((data as unknown as string[]) ?? []).map(String));
}

async function translations(ids: string[], locale: string) {
  if (locale === "en" || !ids.length) return new Map<string, Record<string, unknown>>();
  const { data } = await supabase.from("content_translations").select("record_id,fields").eq("table_name", "industries").eq("locale", locale).in("record_id", ids);
  return new Map((data ?? []).map((x) => [x.record_id, x.fields as Record<string, unknown>]));
}

export async function listLiveIndustries(locale: string): Promise<CmsIndustry[]> {
  return (await loadLiveIndustries(locale)).items;
}

/** Like listLiveIndustries, but distinguishes a failed load (ok=false) from a successful empty result. */
export async function loadLiveIndustries(locale: string): Promise<{ ok: boolean; items: CmsIndustry[]; hidden: Set<string> }> {
  const [{ data, error }, hidden] = await Promise.all([
    supabase.from("industries").select("*").eq("is_published", true).order("sort_order"),
    hiddenStaticIndustrySlugs(),
  ]);
  if (error || !data) return { ok: false, items: [], hidden: new Set() };
  const live = data.filter((r) => isLive(r));
  const T = await translations(live.map((r) => r.id), locale);
  return { ok: true, items: live.map((r) => shape(r, locale, T.get(r.id))), hidden: hidden ?? new Set() };
}

export async function getLiveIndustry(slug: string, locale: string): Promise<{ item: CmsIndustry; links: IndustryLink[] } | null> {
  const { data, error } = await supabase.from("industries").select("*").eq("slug", slug).eq("is_published", true).maybeSingle();
  if (error) throw error;
  if (!data || !isLive(data)) return null;
  const [T, links] = await Promise.all([
    translations([data.id], locale),
    supabase.from("industry_module_links").select("id,module_slug,pain,outcome,sort_order,pricing_addons(slug,name,is_published)").eq("industry_id", data.id).order("sort_order"),
  ]);
  return {
    item: shape(data, locale, T.get(data.id)),
    links: (links.data ?? []).map((l: any) => ({
      id: l.id, module_slug: l.module_slug, pain: l.pain, outcome: l.outcome, sort_order: l.sort_order,
      addon: l.pricing_addons?.is_published ? { slug: l.pricing_addons.slug, name: l.pricing_addons.name } : null,
    })).filter((l) => l.module_slug || l.addon),
  };
}

/** YouTube/Vimeo URL → privacy-friendly embed URL; anything else is shown as a link. */
export function embedUrl(url: string | null): string | null {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?dnt=1`;
  return null;
}
